import { useCallback, useMemo, useRef } from 'react';
import { isBefore, addDays } from 'date-fns';
import isEqual from 'lodash/isEqual';
import { ApiState, AsyncResp, useApi } from '@paradigm/api/src/fetch-api';
import { FilterActions } from '#/leaderboard/contexts/filter-provider-factory';
import useParsedStoredEntry, {
  ParsedStoredEntry,
} from '#/store/useParsedStoredEntry';
import {
  BaseLeaderboardReq,
  BaseLeaderboardResp,
  UserFirm,
} from '#/leaderboard/entities/shared';
import { RawVolumeListResp } from '#/leaderboard/entities/volume';
import { listLeaderboardVolume } from '#/leaderboard/repositories/volume';

interface StoredList<Entity> {
  lastUpdatedAt: number;
  userFirm: UserFirm;
  list: readonly Entity[];
}

interface LeaderboardFetcher<Entity, Req> {
  fetchFn: (req: Req) => AsyncResp<BaseLeaderboardResp<Entity>>;
  filterActions: Pick<
    FilterActions,
    'hidrateStoredFilters' | 'getStoredFilters'
  >;
  key: string;
}

export function useStoredLeaderboardFetcher<
  Entity,
  Req extends BaseLeaderboardReq,
>({ fetchFn, filterActions, key }: LeaderboardFetcher<Entity, Req>) {
  const storedKey = useMemo(() => `leaderboard-${key}`, [key]);
  const storedList = useParsedStoredEntry<StoredList<Entity>>(storedKey);
  const initialState = useMemo(() => getInitialState(storedList), [storedList]);
  const [isLoading, data, error, getLeaderboard] = useLeaderboardFetcher(
    fetchFn,
    initialState,
  );
  const { getStoredFilters, hidrateStoredFilters } = filterActions;
  const ref = useRef(new AbortController());

  const fetchLeaderboard = useCallback(
    (filters: Req) => {
      // Update only if filters change or board data has updated
      if (
        !hasNewDataAvailable(storedList.get()?.lastUpdatedAt ?? null) &&
        isEqual(getStoredFilters(), filters)
      )
        return;

      ref.current.abort();
      ref.current = new AbortController();
      getLeaderboard(
        { ...filters, signal: ref.current.signal },
        (fetchResp) => {
          if (fetchResp.ok) {
            storedList.set({
              lastUpdatedAt: fetchResp.data.last_updated_at,
              list: fetchResp.data.results,
              userFirm: fetchResp.data.user_firm,
            });
            hidrateStoredFilters();
          }
        },
      );
    },
    [getLeaderboard, getStoredFilters, hidrateStoredFilters, storedList],
  );

  const [list, userFirm, lastUpdatedAt] = useMemo(() => {
    if (data != null) {
      const { results, user_firm, last_updated_at } = data;
      return [results, user_firm, last_updated_at];
    }
    return [[], null, null];
  }, [data]);

  return useMemo(
    () =>
      ({
        fetchLeaderboard,
        list,
        userFirm,
        lastUpdatedAt,
        error,
        status: isLoading ? 'loading' : error != null ? 'error' : 'idle',
      } as const),
    [fetchLeaderboard, list, userFirm, lastUpdatedAt, error, isLoading],
  );
}

export const UPDATE_FREQUENCY = 1; // In days
function hasNewDataAvailable(lastUpdatedAt: number | null) {
  if (lastUpdatedAt == null) return true;

  return isBefore(addDays(lastUpdatedAt, UPDATE_FREQUENCY), Date.now());
}

function getInitialState<Entity>(
  storedList: ParsedStoredEntry<StoredList<Entity>>,
): ApiState<BaseLeaderboardResp<Entity>> | undefined {
  const { list, lastUpdatedAt, userFirm } = storedList.get() ?? {};
  if (list == null || lastUpdatedAt == null || userFirm == null)
    return undefined;

  return {
    isLoading: false,
    resp: {
      status: 0,
      ok: true,
      message: '',
      error: '',
      data: {
        count: list.length,
        results: list,
        user_firm: userFirm,
        last_updated_at: lastUpdatedAt,
      },
    },
  };
}

/**
 * Fetch leaderboard data
 * @param initialState
 * @returns [ isLoading, data, error, fetchData ]
 */
export function useLeaderboardFetcher<Entity, Req extends BaseLeaderboardReq>(
  fetchFn: LeaderboardFetcher<Entity, Req>['fetchFn'],
  initialState?: ApiState<BaseLeaderboardResp<Entity>>,
) {
  const [{ isLoading, resp }, executeFetch] = useApi(fetchFn, initialState);

  return [
    isLoading,
    resp?.ok === true ? resp.data : null,
    resp?.ok === false ? resp.message : null,
    executeFetch,
  ] as const;
}

/**
 * Fetch volume leaderboard data
 * @param initialState
 * @returns [ isLoading, data, error, fetchData ]
 */
export function useVolumeLeaderboardFetcher(
  initialState?: ApiState<RawVolumeListResp>,
) {
  return useLeaderboardFetcher(listLeaderboardVolume, initialState);
}
